import { Controller } from "@hotwired/stimulus"

// The Beauftragung (dispatch) confirm dialog. It is injected (already open) into the
// board's #planning_dispatch_dialog container by dispatches#new. This controller handles
// closing (Escape / backdrop / cancel) and the window fields: changing a date (or applying
// a preset) re-fetches the preview so the candidate list always matches the chosen window;
// the form submits as a Turbo Stream, and dispatches#create clears the container on success.
export default class extends Controller {
  static targets = ["root", "from", "to"]
  static values = { previewUrl: String }

  connect() {
    this.boundKeydown = this.onKeydown.bind(this)
    document.addEventListener("keydown", this.boundKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown)
    clearTimeout(this.reloadTimer)
  }

  close() {
    this.element.remove()
  }

  // Where the press STARTED, so a click only counts as a backdrop click when both ends of it
  // are on the backdrop. Selecting the text of a date field by dragging routinely slips past
  // the panel's edge, and the resulting click — whose target is the nearest common ancestor,
  // i.e. the backdrop — used to close the dialog and discard everything the user had entered.
  backdropPress(event) {
    this.pressedOnBackdrop = event.target === this.rootTarget
  }

  backdrop(event) {
    const startedOnBackdrop = this.pressedOnBackdrop
    this.pressedOnBackdrop = false
    if (event.target === this.rootTarget && startedOnBackdrop) this.close()
  }

  // Escape closes the dialog, but only when it is the topmost layer: with a date field's
  // calendar popover open, that keystroke belongs to the calendar. The picker marks the event
  // handled (preventDefault) when it dismisses its own panel; the :popover-open check also
  // covers the native light-dismiss, which closes the popover without touching the event.
  onKeydown(event) {
    if (event.key !== "Escape") return
    if (event.defaultPrevented || this.element.querySelector(":popover-open")) return

    this.close()
  }

  // Set `to` = `from` + N days (N from the button's data-preset).
  // Parsed and formatted in LOCAL time throughout: `new Date("YYYY-MM-DD")` is UTC
  // midnight while setDate is local, and toISOString converts back to UTC — either
  // one shifts the date by a day in negative-offset timezones.
  preset(event) {
    const days = Number(event.currentTarget.dataset.preset)
    const from = this.fromTarget.value ? new Date(`${this.fromTarget.value}T00:00:00`) : new Date()
    from.setDate(from.getDate() + days)
    const pad = (part) => String(part).padStart(2, "0")
    this.toTarget.value = `${from.getFullYear()}-${pad(from.getMonth() + 1)}-${pad(from.getDate())}`
    this.reload()
  }

  // Re-fetch the dialog for the chosen window; the response re-renders the whole dialog, so
  // the candidate list (or the no-candidates state) always matches the visible dates.
  // Debounced: date inputs fire `change` per segment while typing, and an instant re-render
  // would replace the field mid-edit.
  reload() {
    clearTimeout(this.reloadTimer)
    this.reloadTimer = setTimeout(() => this.fetchPreview(), 400)
  }

  async fetchPreview() {
    if (!this.fromTarget.value || !this.toTarget.value) return

    const url = new URL(this.previewUrlValue, window.location.origin)
    url.searchParams.set("from", this.fromTarget.value)
    url.searchParams.set("to", this.toTarget.value)
    // Sequence guard: the debounce spaces requests out but does not stop a slow
    // response for an old window from landing after a fast one — only the
    // latest request may render, or the candidate list contradicts the dates.
    const sequence = (this.fetchSequence = (this.fetchSequence || 0) + 1)
    try {
      const response = await fetch(url, { headers: { Accept: "text/vnd.turbo-stream.html" } })
      if (!response.ok) return

      const markup = await response.text()
      if (sequence !== this.fetchSequence) return

      Turbo.renderStreamMessage(markup)
    } catch {
      // Network failure: keep the last rendered list instead of crashing with
      // an unhandled rejection; the next date change retries anyway.
    }
  }
}
